import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, Percent, Clock, Euro, Tag, User } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatCurrency, formatCurrency2, formatNumber, formatDate } from '../lib/format';
import { Loader, ErrorBox } from './charts';

/**
 * The receipts behind a KPI card: every ticket in the window with its total, a
 * one-line summary of what was on it, and the discount plus who rang it up —
 * which is how a staff ticket gives itself away. Rows expand to their items.
 *
 * Opened from the Overzicht KPI tiles and from a row in "Dag per dag".
 */
export default function ReceiptsModal({ start, end, label, onClose }) {
  const t = useT();
  const [q, setQ] = useState('');
  const [onlyDiscount, setOnlyDiscount] = useState(false);
  const [sort, setSort] = useState('time');      // time | amount
  const [open, setOpen] = useState(null);        // expanded order_id
  const panel = useRef(null);

  const res = useApi(() => api.receiptsList({ start_date: start, end_date: end }), [start, end]);
  const d = res.data;

  // Escape closes; the page behind must not scroll while the sheet is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const rows = useMemo(() => {
    let list = d?.receipts || [];
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((r) => `${r.description} ${r.staff} ${r.table} ${r.payment} ${r.time}`
        .toLowerCase().includes(needle));
    }
    if (onlyDiscount) list = list.filter((r) => r.discount > 0);
    return sort === 'amount' ? [...list].sort((a, b) => b.amount - a.amount) : list;
  }, [d, q, onlyDiscount, sort]);

  const tot = d?.totals;
  const stat = (icon, labelText, value) => (
    <div className="flex items-center gap-1.5 min-w-0">
      {icon}
      <span className="text-slate-400">{labelText}</span>
      <span className="font-semibold tabular-nums text-slate-700">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center print:hidden"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" aria-hidden="true" />
      <div ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t('Bonnen')}
        className="relative w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white shadow-2xl outline-none">

        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>
              {t('Bonnen')}{label ? ` — ${label}` : ''}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('Sluiten')}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {tot && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-xs border-b border-slate-100 bg-slate-50/60">
            {stat(<Tag className="h-3.5 w-3.5 text-slate-300" />, t('Bonnen'), formatNumber(tot.receipts))}
            {stat(<Euro className="h-3.5 w-3.5 text-slate-300" />, t('Omzet'), formatCurrency(tot.revenue))}
            {stat(<Clock className="h-3.5 w-3.5 text-slate-300" />, t('Gem. per bon'), formatCurrency2(tot.avg_per_receipt))}
            {tot.discount > 0 && stat(<Percent className="h-3.5 w-3.5 text-slate-300" />, t('Korting'),
              `${formatCurrency(tot.discount)} (${formatNumber(tot.discounted_receipts)} ${t(tot.discounted_receipts === 1 ? 'bon' : 'bonnen')})`)}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-slate-100">
          <div className="relative flex-1 min-w-[140px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-300" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Zoek op item, tafel of personeel')}
              className="w-full rounded-md border border-slate-200 pl-7 pr-2 py-1.5 text-[16px] sm:text-xs outline-none focus:border-[#cf9a40]" />
          </div>
          <button type="button" onClick={() => setOnlyDiscount((v) => !v)}
            className={`rounded-md border px-2 py-1.5 text-[11px] font-medium ${onlyDiscount
              ? 'border-[#cf9a40] bg-[#cf9a40]/10 text-[#8a6320]' : 'border-slate-200 text-slate-500 hover:text-slate-700'}`}>
            {t('Met korting')}
          </button>
          <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5 text-[11px] font-medium">
            {[{ k: 'time', l: t('Tijd') }, { k: 'amount', l: t('Bedrag') }].map((o) => (
              <button key={o.k} type="button" onClick={() => setSort(o.k)}
                className={`px-2 py-1 rounded ${sort === o.k ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500'}`}>{o.l}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          {res.loading && <Loader />}
          {res.error && <div className="p-4"><ErrorBox error={res.error} onRetry={res.reload} /></div>}
          {d && !rows.length && (
            <p className="px-4 py-8 text-center text-sm text-slate-400">
              {d.totals.receipts ? t('Geen bonnen die aan de filter voldoen.') : t('Nog geen bonnen in deze periode.')}
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => {
              const isOpen = open === r.order_id;
              return (
                <li key={r.order_id}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : r.order_id)}
                    aria-expanded={isOpen} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-3">
                    <span className="tabular-nums text-xs font-medium text-slate-500 w-10 shrink-0 pt-0.5">{r.time}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-slate-700 truncate">{r.description || t('(geen items)')}</span>
                      <span className="block text-[11px] text-slate-400 truncate">
                        {formatNumber(r.items)} {t(r.items === 1 ? 'artikel' : 'artikelen')}
                        {r.profile && ` · ${r.profile}`}
                        {r.payment && ` · ${r.payment}`}
                        {r.table && ` · ${r.table}`}
                        {r.staff && <> · <User className="inline h-3 w-3 -mt-0.5" /> {r.staff}</>}
                      </span>
                    </span>
                    <span className="text-right shrink-0">
                      <span className="block text-sm font-semibold tabular-nums text-slate-800">{formatCurrency2(r.amount)}</span>
                      {r.discount > 0 && (
                        <span className="inline-block mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium bg-[#cf9a40]/15 text-[#8a6320] tabular-nums">
                          −{formatCurrency2(r.discount)}
                        </span>
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <ul className="px-4 pb-3 pl-[4.25rem] space-y-1">
                      {r.lines.map((l, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-slate-500 truncate">
                            {l.qty > 1 && <span className="tabular-nums">{formatNumber(l.qty)}× </span>}{l.name}
                          </span>
                          <span className="tabular-nums text-slate-600 shrink-0">{formatCurrency2(l.amount)}</span>
                        </li>
                      ))}
                      {!r.lines.length && <li className="text-xs text-slate-400">{t('(geen items)')}</li>}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          {d?.truncated && (
            <p className="px-4 py-3 text-[11px] text-slate-400 border-t border-slate-100">
              {t('Alleen de meest recente bonnen worden getoond; de totalen hierboven dekken de hele periode.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
